import './CancellationModal.less';
import { Button, Modal, Radio, Input, Alert } from 'antd';
import React, { useMemo, useState } from 'react';
import { shuffleArray } from '../shared/utils';
import { RadioChangeEvent } from 'antd/lib/radio';

const { TextArea } = Input;

const reasons = [
    'It was too costly',
    'Needed more features',
    'It was too complicated to use',
    'I am using another search engine analyzer',
    'Customer support was low',
    'There were technical issues',
    'It is not serving my main purpose',
];

type Props = {
    title: string;
    showInfo?: boolean;
    showFreeTrialInfo?: boolean;
    isLoading: boolean;
    onConfirmCancellation: (reason: string, comment: string) => Promise<void>;
    onClose: () => void;
};

export default function CancellationModal({ title, showInfo, showFreeTrialInfo, isLoading, onClose, onConfirmCancellation }: Props) {
    const [reason, setReason] = useState('');
    const [comment, setComment] = useState<string>('');
    const [cancellationAlert, setCancellationAlert] = useState<string>('');

    const handleReasonChange = (event: RadioChangeEvent) => {
        setCancellationAlert('');
        setReason(event.target.value);
    };

    const handleCommentChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setCancellationAlert('');
        setComment(event.target.value);
    };

    const handleCancelButtonClicked = async () => {
        const trimmedComment = comment.trim();

        if (!reason || !trimmedComment) {
            setCancellationAlert('Please describe why are you cancelling your subscription');
            return;
        }

        await onConfirmCancellation(reason, trimmedComment);
        onClose();
    };

    const shuffledReasons = useMemo(() => [...shuffleArray(reasons), 'I have another reason'], []);

    const isConfirmDisabled = !reason || !comment;

    return (
        <Modal
            width={756}
            visible
            title={title}
            onCancel={onClose}
            destroyOnClose
            footer={[
                <Button
                    key="return"
                    onClick={onClose}
                >
                    Not sure about this
                </Button>,
                <Button
                    key="cancel-subscription"
                    type="primary"
                    loading={isLoading}
                    disabled={isConfirmDisabled}
                    onClick={handleCancelButtonClicked}
                >
                    Confirm
                </Button>,
            ]}
        >
            <div className="cancellation-reasons">
                {cancellationAlert && (
                    <Alert
                        type="error"
                        message={cancellationAlert}
                    />
                )}
                {showInfo && (
                    <div>
                        {/* <h1>Hope to see you back!</h1> */}
                        {showFreeTrialInfo && (
                            <p>
                                Need more time for your free trial? Please contact us for an extension:{' '}
                                <a
                                    style={{ display: 'inline' }}
                                    href="mailto:information@seositecheckup.com"
                                >
                                    information@seositecheckup.com
                                </a>
                            </p>
                        )}
                        <p>
                            We care about each step of our collaboration, that's why we have made the unsubscribe process also easy and secure. Despite your
                            leaving, don't forget your account is still available until the subscription period ends without any extra charge of your card. You
                            can still enjoy SEO Site Checkup features till then.
                        </p>
                    </div>
                )}
                <h2>Why are you leaving?</h2>
                <Radio.Group
                    onChange={handleReasonChange}
                    value={reason}
                >
                    {shuffledReasons.map((reason) => {
                        return (
                            <Radio
                                key={reason}
                                style={{ display: 'block', height: '30px', lineHeight: '30px' }}
                                value={reason}
                            >
                                {reason}
                            </Radio>
                        );
                    })}
                </Radio.Group>
                <div className="textarea-label">Comment: </div>
                <TextArea
                    name="comment"
                    onChange={handleCommentChange}
                    value={comment}
                    rows={4}
                />
            </div>
        </Modal>
    );
}
